import Phaser from "phaser";
import {Stats} from "./Stats";
import {MonsterFrames} from "./MonsterFrames";
import {createDissolveEffect} from "./Dissolve";
import {damageColor} from "./DamageColor";
import {uiBorderColor} from "./Colors";
import {MonsterType} from "./CardTypes";
import {getBarShifts} from "./MonsterBarShifts";
import {attackFrameInterval, defaultFrameInterval} from "./Timings";

export class Monster extends Phaser.Physics.Arcade.Sprite {
    private idleFrames: string[]
    private attackFrames: string[]
    private attackSound: {
        sound: string,
        volume: number
    }
    private stats!: Stats
    private particleContainer: Phaser.GameObjects.Container;
    private isAttacking: boolean = false;
    private isDamaging: boolean = false;
    private currentFrameIndex: number = 0;
    private lastTime: number = 0;
    private lastFrameTime: number = 0;
    private healthBox: Phaser.GameObjects.Graphics
    private boxWidth: number = 32
    private boxHeight: number = 2

    constructor(scene: Phaser.Scene, x: number, y: number, stats: Stats, monsterFrames: MonsterFrames,
                particleContainer: Phaser.GameObjects.Container,
                attackSound: {
                    sound: string,
                    volume: number
                }, private monsterType: MonsterType
                ) {
        super(scene, x, y, monsterFrames.idleFrames[0]);
        this.idleFrames = monsterFrames.idleFrames
        this.attackFrames = monsterFrames.attackFrames
        scene.add.existing(this);
        scene.physics.add.existing(this);
        this.setTexture(this.idleFrames[0]);
        this.setDepth(10000);
        this.setAlpha(0);

        this.stats = stats;

        scene.events.once('shutdown', () => {
            scene.data.remove('particleContainer');
        });

        this.createFadeInEffect();
        this.particleContainer = particleContainer

        this.healthBox = scene.add.graphics();
        this.healthBox.setScrollFactor(0);
        this.healthBox.setDepth(1000);

        this.attackSound = attackSound
    }

    private drawHealth() {
        const { width, height } = this.scene.scale;

        this.healthBox.clear();

        const monsterBarShifts = getBarShifts(this.monsterType)
        const boxX = width - 130 + monsterBarShifts.healthX
        const boxY = 116 + monsterBarShifts.healthY

        this.healthBox.lineStyle(2, uiBorderColor);
        this.healthBox.strokeRect(boxX, boxY, this.boxWidth, this.boxHeight);

        this.healthBox.fillStyle(0x00ff00);
        const playerFillWidth = (Math.max(this.stats.health, 0.0) / this.stats.maxHealth) * this.boxWidth;
        this.healthBox.fillRect(boxX, boxY, playerFillWidth, this.boxHeight);
    }

    private createFadeInEffect() {
        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            duration: 250,
            ease: 'Linear',
            onComplete: () => {
                this.setAlpha(1);
            }
        });
    }

    startAttacking(): void {
        if (!this.isAttacking) {
            this.isAttacking = true
            this.currentFrameIndex = 1;
            this.setTexture(this.attackFrames[this.currentFrameIndex]);
            this.lastFrameTime = this.lastTime
        }

        this.scene.sound.play(this.attackSound.sound, {
            loop: false,
            volume: this.attackSound.volume
        })
    }

    stopAttacking(): void {
        if (this.isAttacking) {
            this.isAttacking = false;
            this.currentFrameIndex = 0;
            this.setTexture(this.idleFrames[0]);
        }
    }

    startDamaging(level: number = 0): void {
        if (!this.isDamaging) {
            this.isDamaging = true;
            this.setTint(damageColor);
        }

        this.scene?.time.delayedCall(100, () => {
            this.stopDamaging()

            if (level < 2) {
                this.scene?.time.delayedCall(100, () => {
                    this.startDamaging(level + 1)
                })
            }
        })
    }

    stopDamaging(): void {
        if (this.isDamaging) {
            this.isDamaging = false;
            this.setTint(0xffffff);
        }
    }

    update(time: number, delta: number): void {
        let targetFrames: string[]

        if (this.isAttacking) {
            targetFrames = this.attackFrames
        }
        else {
            targetFrames = this.idleFrames
        }

        if (time - this.lastFrameTime > (this.isAttacking ? attackFrameInterval : defaultFrameInterval)) {
            this.lastFrameTime = time;
            this.currentFrameIndex = (++this.currentFrameIndex) % targetFrames.length;
            this.setTexture(targetFrames[this.currentFrameIndex]);

            if (this.isAttacking && this.currentFrameIndex === 0) {
                this.stopAttacking()
            }
        }

        if (this.isDamaging) {
            this.setTint(damageColor);
        }
        else {
            this.setTint(0xffffff);
        }

        this.lastTime = time
        this.drawHealth()
    }

    public getStats(): Stats {
        return this.stats;
    }

    dissolve() {
        createDissolveEffect(this, this.particleContainer)
    }

    destroy(fromScene?: boolean) {
        this.setVisible(false);
        this.healthBox.visible = false
        super.destroy(fromScene);
    }

    getType() {
        return this.monsterType
    }
}
